import React from 'react';

export function LandingContent() {
  return (
    <div className="max-w-4xl mx-auto mt-24 mb-32 px-6 text-white/80 space-y-16">
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold text-white">Humanize AI Text: The Ultimate Academic Synthesis Engine</h2>
        <p className="text-sm leading-relaxed">
          Transform your machine-generated essays, project reports, and empirical coursework into natural, human-authored prose with our advanced stylometric rewriting platform. We move beyond basic synonym swapping or rudimentary spinning. Our sophisticated algorithmic humanization tool ingests your contextual grading rubrics, peer-reviewed references, and authentic writing samples to seamlessly convert output from large language models (LLMs) like ChatGPT, Claude, and Gemini into nuanced literature. This ensures a guaranteed 0% artificial authorship probability, securing your academic integrity and bypassing the most rigorous detection algorithms.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Elevating Academic Authenticity and Rigor</h2>
        <p className="text-sm leading-relaxed mb-4">
          Academic evaluators demand depth, nuance, and grounded assertions. Our platform is specifically designed to elevate the natural flow and intellectual authority of your writing by empowering you to:
        </p>
        <ol className="space-y-3 text-sm list-decimal list-outside ml-5 pl-2 marker:text-[#00ff00] marker:font-bold">
          <li className="pl-2">
            <strong className="text-white block mb-1">Share first-hand experiences or case studies:</strong>
            <span className="text-white/60">Upload your own previous writing samples so our engine contextualizes subjective viewpoints, mimicking your unique experiential footprint.</span>
          </li>
          <li className="pl-2">
            <strong className="text-white block mb-1">Include statistics and cite reputable sources:</strong>
            <span className="text-white/60">Utilize our Auto Citation engine to ground assertions with empirical data and accurate academic formatting (e.g., Harvard, APA).</span>
          </li>
          <li className="pl-2">
            <strong className="text-white block mb-1">Showcase your credentials or industry expertise:</strong>
            <span className="text-white/60">Map advanced lexical tiers and specialized jargon that demonstrate deep subject-matter comprehension and professional authority.</span>
          </li>
          <li className="pl-2">
            <strong className="text-white block mb-1">Present multiple perspectives on complex topics:</strong>
            <span className="text-white/60">Leverage nuanced phrasing parameters to evaluate counterarguments, fostering balanced and intellectually rigorous discourse.</span>
          </li>
          <li className="pl-2">
            <strong className="text-white block mb-1">Address potential objections or limitations:</strong>
            <span className="text-white/60">The humanization process encourages critical self-reflection within the text, preemptively acknowledging factual boundary conditions.</span>
          </li>
        </ol>
      </section>

      <section className="space-y-4 bg-zinc-900/30 p-6 rounded-xl border border-white/5">
        <h2 className="text-xl font-semibold text-white mb-4">Frequently Asked Questions (FAQ)</h2>
        <div className="space-y-5 text-sm">
          <div>
            <strong className="text-white block mb-1">What distinguishes this platform from a standard text spinner?</strong>
            <p className="text-white/60">Standard spinners arbitrarily swap synonyms, resulting in robotic, anomalous phrasing. Our engine utilizes deep stylometric modeling, adjusting structural burstiness, syntactic variation, and clause density to reflect authentic human cognitive processes.</p>
          </div>
          <div>
            <strong className="text-white block mb-1">Can it consistently bypass enterprise AI checkers like Turnitin and GPTZero?</strong>
            <p className="text-white/60">Yes. By strategically modulating entropy and avoiding predictive algorithmic tokens (like "delve", "testament", or "tapestry"), the output registers as genuinely human-authored under adversarial scrutiny.</p>
          </div>
          <div>
            <strong className="text-white block mb-1">How does the citation generation maintain academic rigor?</strong>
            <p className="text-white/60">The proprietary Auto Citation system cross-references claims against established databases and peer-reviewed indices, ensuring bibliographies are factually grounded and properly formatted to prevent academic misconduct.</p>
          </div>
        </div>
      </section>

      <div className="text-center text-xs text-white/40 pt-4 border-t border-white/5">
         Secure checkout supported by Stripe. We accept Credit Cards, Apple Pay, and Google Pay.
      </div>
    </div>
  );
}
